import type { Guide } from "./guide";

/** Guide 2: client tools answered by your code, the module the CLI loads, fixtures in tests, MCP servers with their tool policy (R56), and a test that says yes. */
export const YOUR_TOOLS: Guide = {
  slug: "your-tools",
  samples: "tools",
  title: "Give Bash your tools",
  nav: "Your tools",
  summary:
    "Let Bash reach your own systems: tools your code answers, fixtures that play them in tests, and MCP servers.",
  minutes: 10,
  lede: "Your tools let Bash reach your systems: a booking, an order, a ticket. Bash decides when to call one, and your code answers it. This uses the help desk from [the first guide](/developers/guides/travel-help-desk).",
  lines: ["deploy", "ask", "answer", "events", "test"],
  steps: [
    {
      id: "declare",
      title: "Declare the tool",
      body: [
        "Bash calls only what your file declares, under a name that is not one of its own tools (`execute_code`, `mcp_invoke` or `remember`, say, refused with `400 tool_name_reserved` whether or not your deployment uses that tool), and the description tells Bash when to call it. A tool that changes something waits for your user's yes before its call reaches your code: mark a lookup `read_only: true`, and a change that needs no yes `run_without_asking: true`, and either runs at once.",
      ],
      file: "tools.yaml",
      then: "On your next deploy, Bash knows `get_booking`, which runs at once, and `extend_rental`, which waits for your user's yes on an approval that shows every input it is called with, so it takes the price and the new return time the user agrees to, never only the rental. Your user reads a description's first sentence on the approval, and drafted tests are written to copy the result it says the tool returns: one example in your code's field names and formats, with made-up values (`Returns {customer: 'Maya Chen', due_back: '2026-10-04 18:00'}`).",
    },
    {
      id: "answer",
      title: "Answer from your code",
      body: [
        "A function per tool in the SDK, or a module for the CLI. Over HTTP, a call is a `tool_call` you answer by its id.",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-ask" },
        py: { sample: "guide-helpdesk-ask" },
        cli: { sample: "guide-helpdesk-ask" },
        http: { sample: "guide-helpdesk-ask" },
        mcp: {
          note: "An MCP client's own tools stay with its own agent. The tools Bash calls in your systems are answered by your code: from an SDK, the CLI or over HTTP.",
        },
      },
      then: "What your function returns is the call's output, and a throw is its error, which Bash reads.",
      prints: "  your tool get_booking was called: your module answers it",
    },
    {
      id: "module",
      title: "Write the tools module",
      body: [
        "`--tools ./tools.mjs` loads one module for `vestra ask` and `vestra dev`: a function per tool, exported under the tool's name, as named exports or as the default export's keys. Each function gets the call's arguments as one object, and the call (`{ id, name, conversation, task, user }`) beside them.",
      ],
      file: "your-tools.mjs",
      fileAs: "tools.mjs",
      then: "A function that returns nothing answers `null`, and one that returns something JSON cannot hold answers an error. `extend_rental` checks the price and the return time the user said yes to against your own records, and a throw when one differs charges nothing.",
    },
    {
      id: "server",
      title: "Or answer from your server",
      body: [
        "Once your server takes Bash's webhooks, it answers a call when none of your code is running: a routine at night, or Bash inside your app. Answer each tool from one place, because if your code and your server both answer, both run the call.",
      ],
      doors: {
        ts: { sample: "guide-tools-webhook" },
        py: { sample: "guide-tools-webhook" },
        cli: { sample: "guide-tools-webhook" },
        http: { sample: "guide-tools-webhook" },
        mcp: {
          note: "Webhooks are set up once, from an SDK, the CLI or over HTTP.",
        },
      },
      then: "A call arrives as `interaction.created`, type `tool_call`, and the handler answers it. One nobody answers expires after its `timeout_s`.",
    },
    {
      id: "fixtures",
      title: "Play it in tests",
      body: [
        "A test cannot wait for your code, so its `tool_results` answer for it, and Bash drafts tests with them ([`test`](/developers/test)).",
      ],
      doors: {
        ts: { sample: "guide-tools-fixtures" },
        py: { sample: "guide-tools-fixtures" },
        cli: { sample: "guide-tools-fixtures" },
        http: { sample: "guide-tools-fixtures" },
        mcp: {
          note: "Drafting tests is done from an SDK, the CLI or over HTTP. An agent runs them with the `test` tool.",
        },
      },
      then: "Each drafted test says what `get_booking` answers in it. A tool with no fixture answers with an error.",
      prints:
        "Added 3 tests to vestra.yaml. Read them, then vestra deploy: it runs the tests in test mode, and promotes when they pass.",
    },
    {
      id: "mcp",
      title: "Connect an MCP server",
      body: [
        "Bash calls its tools itself, and each waits for your user's yes unless your server marks it read-only (`readOnlyHint`) or your file's `run_without_asking` lists it. `deny`, `require_approval` and `run_without_asking` in your file apply to the version you deploy; `deny` and `require_approval` set on the server itself hold for every version, whatever the file says ([`deploy`](/developers/deploy)).",
      ],
      file: "mcp-server.yaml",
      then: "A denied tool never runs, a read-only tool or one that runs without asking runs at once, and every other tool waits for your user's yes. Bash names the server by its slug, made from its URL's host (`mcp-tripnest-example`), never by its `name`, and never as one of its own servers (`composio`, `browser-control`, `os-hands`, `vestra`).",
    },
    {
      id: "yes",
      title: "Test the yes",
      body: [
        "The test user says no to every approval, so a test of a tool that waits checks that Bash asks first and, after the no, says the action did not happen. With `approves: true` it says yes to each approval for a tool of your MCP server or one of your functions that waits, as your user would in your app, and the tool's `tool_results` entry, keyed `<slug>.<tool>` for a server's tool or by the function's name, answers in its place.",
      ],
      file: "a-test-yes.yaml",
      then: "Bash asks, the test user approves, the fixture answers, and the judge reads what Bash told the traveller. Anything else that waits for a yes, such as a browser action on an account, is still declined.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: [
        "Your tool, answered in a run, at your server and in your tests, in one program.",
      ],
      doors: {
        ts: { sample: "guide-tools-whole" },
        py: { sample: "guide-tools-whole" },
        cli: { sample: "guide-tools-whole" },
        http: { sample: "guide-tools-whole" },
        mcp: {
          note: "Your tools are answered by your code: from an SDK, the CLI or over HTTP.",
        },
      },
      then: "Bash reaches your systems only through your code, and your tests play them without it.",
    },
  ],
};
